import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import { useAuth } from './AuthContext';
import API_BASE from '../utils/api';

const ChatContext = createContext(null);
export const useChat = () => useContext(ChatContext);

const SOCKET_URL = API_BASE.replace('/api', '');
const CHAT_ROLES = ['admin', 'principal', 'class_teacher'];

// Short two-tone "ping" synthesized with Web Audio API — avoids shipping an audio asset.
let sharedAudioCtx = null;
const playNotificationSound = () => {
  try {
    if (!sharedAudioCtx) {
      sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = sharedAudioCtx;
    if (ctx.state === 'suspended') ctx.resume();

    [880, 1320].forEach((freq, i) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = freq;
      const startAt = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(0.2, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.18);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(startAt);
      oscillator.stop(startAt + 0.2);
    });
  } catch (_) {}
};

export const ChatProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [incomingToast, setIncomingToast] = useState(null);
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());

  const isChatUser = isAuthenticated && user && CHAT_ROLES.includes(user.role);

  const refreshUnreadCount = useCallback(() => {
    axios.get(`${API_BASE}/chat/unread-count`)
      .then(res => setUnreadCount(res.data.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isChatUser) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      // Request current online list immediately on connect / reconnect
      socket.emit('get_online_users');
      refreshUnreadCount();
    });

    socket.on('online_users', (ids) => setOnlineUsers(ids || []));

    socket.on('new_message', (msg) => {
      const myId = user?.id?.toString() || user?._id?.toString();
      const receiverId = msg.receiverId?.toString?.() || msg.receiverId;
      const senderId = msg.senderId?.toString?.() || msg.senderId;

      const isForMe = receiverId === myId;
      const isFromMe = senderId === myId;

      if (isForMe) {
        setUnreadCount(c => c + 1);
        setIncomingToast(msg);
        playNotificationSound();
      }

      // Fan out to all Chat.jsx subscribers
      listenersRef.current.forEach(fn => fn(msg, { isForMe, isFromMe }));
    });

    // Initial unread count
    refreshUnreadCount();

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isChatUser, user?.id, refreshUnreadCount]);

  // Subscribe to incoming messages (used by Chat.jsx)
  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const decrementUnread = useCallback((n = 1) => {
    setUnreadCount(c => Math.max(0, c - n));
  }, []);

  const clearToast = useCallback(() => setIncomingToast(null), []);

  const isOnline = useCallback(
    (id) => onlineUsers.includes(id?.toString()),
    [onlineUsers]
  );

  return (
    <ChatContext.Provider value={{
      socket: socketRef,
      unreadCount,
      onlineUsers,
      incomingToast,
      subscribe,
      decrementUnread,
      refreshUnreadCount,
      clearToast,
      isOnline,
    }}>
      {children}
    </ChatContext.Provider>
  );
};
